import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import React from 'react';

import { SafeNotes } from './HistoryLogRenderers';
import { InteractionFieldRows } from './InteractionFieldRows';
import { parseInteractionLog } from '../../../../../components/organisms/InteractionAnswers';

// Re-exported because this panel is the documented entry point for reading a history
// log's interaction answers; the parse itself is shared with the mobile timeline.
export { parseInteractionLog };

/**
 * One interaction attached to a history entry: the interaction's own name as the
 * heading, then its answers.
 *
 * The note text is deliberately not rendered when there is structured data. The BFF
 * writes the same answers into the note as prose ("Merchant Signature :
 * 2134841-signature-1.png successfully uploaded"), so showing both said everything
 * twice — and the structured version is the better of the two, because it can show a
 * signature as the signature and a photo as the photo rather than as the name of a
 * file.
 */
export const InteractionLogEntry: React.FC<{
  notes: string;
  data: unknown;
  serviceId?: number;
}> = ({ notes, data, serviceId }) => {
  const parsed = parseInteractionLog(data);
  const heading = parsed?.title || 'Interaction';
  const hasFields = !!parsed?.fields.length;

  return (
    <Box sx={{ mt: 1, pl: 1.5, borderLeft: '3px solid', borderColor: 'v4.accent', py: 0.5 }}>
      <Typography sx={{ fontSize: '11px', fontWeight: 700, color: 'v4.accent', mb: 0.25 }}>
        {heading}
      </Typography>
      {hasFields ? (
        <InteractionFieldRows fields={parsed.fields} serviceId={serviceId} />
      ) : (
        // No structured answers to show — fall back to whatever the BFF wrote, so
        // the entry is never blank.
        <SafeNotes html={notes} />
      )}
    </Box>
  );
};

/**
 * Interaction answers shown beneath a history entry's own note, indented and
 * quieter. Used where the note is the primary message and the answers are
 * supporting detail, rather than the entry being the interaction itself.
 */
export const InteractionDetails: React.FC<{ data: unknown; serviceId?: number }> = ({
  data,
  serviceId,
}) => {
  const parsed = parseInteractionLog(data);
  if (!parsed) return null;
  if (!parsed.fields.length && !parsed.title) return null;

  return (
    <Box
      sx={{
        mt: 0.5,
        pl: 1,
        borderLeft: '2px solid',
        borderColor: 'v4.border',
        fontSize: '11px',
        color: 'text.secondary',
      }}
    >
      {parsed.title && (
        <Typography sx={{ fontSize: '11px', fontWeight: 600, mb: 0.25 }}>{parsed.title}</Typography>
      )}
      <InteractionFieldRows fields={parsed.fields} serviceId={serviceId} compact />
    </Box>
  );
};
